<template>
  <div class="card-maquina" :style="cardBorderStyle">
    <div class="side-status-bar" :style="{ background: colorEstatus(item.status) }"></div>
    <div class="card-header">
      <div class="machine-id-block">
        <div class="machine-label-row">
          <v-icon size="13" :color="colorEstatus(item.status)" class="mr-1 machine-cog-icon">mdi-cog</v-icon>
          <span class="machine-label">Máquina</span>
        </div>
        <span class="machine-code">{{ item.codigoMaquina }}</span>
      </div>
      <div class="header-right">
        <div class="status-pill" :style="pillStyle">
          <span class="status-led" :style="{ background: colorEstatus(item.status) }"></span>
          {{ labelEstatus(item.status) }}
        </div>
        <div v-if="item.activaSinOdt" class="activa-badge">
          <span class="activa-led"></span>
          Activa sin ODT
        </div>
      </div>
    </div>

    <template v-if="!isInactive">

      <div v-if="showOeeSection" class="oee-section">
        <div class="ring-wrap">
          <svg viewBox="0 0 90 90" width="90" height="90" style="transform:rotate(-90deg)">
            <circle cx="45" cy="45" r="36" fill="none" stroke="#F0F2F6" stroke-width="7"/>
            <circle
              cx="45" cy="45" r="36" fill="none"
              :stroke="colorEstatus(item.status)"
              stroke-width="7"
              stroke-linecap="round"
              :stroke-dasharray="`${oeeArc} ${circumference}`"
            />
          </svg>
          <div class="ring-center">
            <template v-if="item.oee != null">
              <ICountUp class="ring-value" :endVal="item.oee" :options="{ decimalPlaces: 0 }" />
              <span class="ring-pct">%</span>
            </template>
            <template v-else>
              <span class="ring-value">—</span>
            </template>
            <div class="ring-sub">OEE</div>
          </div>
        </div>

        <div class="kpi-trio">
          <div class="kpi-row" v-for="k in kpis" :key="k.name">
            <div class="kpi-label-block">
              <div class="kpi-dot" :style="{ background: k.color }"></div>
              <span class="kpi-name">{{ k.name }}</span>
            </div>
            <div class="kpi-bar-wrap">
              <div class="kpi-bar-fill" :style="{ width: k.val + '%', background: k.color }"></div>
            </div>
            <span class="kpi-value">{{ k.display }}</span>
          </div>
        </div>
      </div>

      <!-- TC CHIPS -->
      <div v-if="showTcRow" class="tc-row">
        <div class="tc-chip">
          <span class="tc-chip-label">TC Planeación</span>
          <span class="tc-chip-value qad">{{ tcDisplay(item.tcProgramadoTxt) }}</span>
        </div>
        <div class="tc-chip">
          <span class="tc-chip-label">TC Real</span>
          <span v-if="item.tcRealCargando" class="tc-chip-value">
            <v-progress-circular indeterminate size="12" width="2" color="#8A95A3" />
          </span>
          <span v-else class="tc-chip-value">{{ tcDisplay(item.tcRealTxt) }}</span>
        </div>
        <div class="tc-chip">
          <span class="tc-chip-label">TC Cotizado</span>
          <span class="tc-chip-value">{{ tcDisplay(item.tcRecetaTxt) }}</span>
        </div>
      </div>

      <div class="h-divider"></div>

      <div class="info-section">
        <template v-if="item.status >= 1 && nombreEmpresa === 'TAMTO'">
          <div class="info-row">
            <span class="info-key">Artículo</span>
            <span class="info-val">{{ item.nombreArticulo }}</span>
          </div>
          <div class="info-row">
            <span class="info-key">ODT</span>
            <span class="info-val">{{ limpiarOT(item.ot) }}</span>
          </div>
        </template>

        <div v-if="showCavidades" class="cavidades-block">
          <div
            v-for="(ca, i) in item.cavidadesArticulos"
            :key="'cav-'+i"
            class="cavidades-item"
          >
            <span class="cavidades-dot"></span>
            <span class="cavidades-art">{{ ca.nombre }}</span>
            <span class="cavidades-badge">{{ ca.cavidades }} {{ ca.cavidades === 1 ? 'cav.' : 'cavs.' }}</span>
          </div>
        </div>

        <div v-if="item.codigoMolde" class="info-row info-row-molde">
          <span class="info-key">
            <v-icon size="12" color="#8A95A3">mdi-package-variant</v-icon>
            Molde
          </span>
          <span class="info-val molde-val">{{ item.codigoMolde }}</span>
        </div>

        <div v-if="item.status !== 2 && isCazel && item.piezasScrap > 0" class="info-row">
          <span class="info-key">
            <v-icon size="12" color="#8A95A3">mdi-alert-circle-outline</v-icon>
            Piezas No OK
          </span>
          <span class="info-val">{{ item.piezasScrap != null ? item.piezasScrap : 0 }} pzas</span>
        </div>
      </div>

      <!-- BLOQUE DE PARO -->
      <div v-if="showParoBlock" class="paro-section" :class="{ 'paro-np': item.status === 4 }">
        <p class="paro-title">Paro en curso</p>
        <div class="paro-timer">
          <span class="paro-timer-hms">{{ timerHMS }}</span><span class="paro-timer-ms">.{{ timerMS }}</span>
        </div>
        <p class="paro-meta">
          Inicio: {{ item.inicioParo || '—' }} · Piezas Ok: {{ item.pizasOk }}
        </p>
        <div v-if="item.paroTipo" class="paro-tag" :class="item.status === 4 ? 'tag-red' : 'tag-amber'">
          {{ item.paroTipo }}<template v-if="item.paroSubTipo"> · {{ item.paroSubTipo }}</template>
        </div>
      </div>

      <!-- PRODUCCIÓN (status activo) -->
      <div v-if="item.status === 1 && !item.activaSinOdt" class="prod-section">
        <div class="prod-box">
          <span class="prod-box-label">Piezas Ok</span>
          <span class="prod-box-value">{{ item.pizasOk }}</span>
        </div>
        <div v-if="isCazel" class="prod-box prod-box-no-ok">
          <span class="prod-box-label">Piezas No OK</span>
          <span class="prod-box-value">{{ item.piezasScrap != null ? item.piezasScrap : 0 }}</span>
        </div>
        <div v-if="!isCazel" class="prod-box">
          <span class="prod-box-label">Ok por OT</span>
          <span class="prod-box-value blue">{{ item.pizasOkPorOrden }}</span>
        </div>
        <div class="prod-box">
          <span class="prod-box-label">{{ isCazel ? 'Pzas Programadas' : 'Objetivo Turno' }}</span>
          <span class="prod-box-value">{{ item.planeacionPorTurno }}</span>
        </div>
      </div>

    </template>

    <template v-else>
      <div class="inactive-state">
        <div class="inactive-icon">
          <v-icon size="22" color="#8A95A3">mdi-minus-circle-outline</v-icon>
        </div>
        <p class="inactive-text">Sin ODT</p>
      </div>
    </template>

  </div>
</template>

<script>
import moment from "moment";
import ICountUp from "vue-countup-v2";
import { mapState } from 'vuex';

const R = 36;
const CIRCUMFERENCE = 2 * Math.PI * R;

const STATUS_MAP = {
  1: { label: 'Activa',          color: '#00C472' },
  2: { label: 'Sin ODT',         color: '#2B7EFF' },
  3: { label: 'Paro Programado', color: '#FF9F00' },
  4: { label: 'Paro No Prog.',   color: '#E83535' },
};

export default {
  name: 'CardMaquina',
  components: { ICountUp },

  props: {
    item: {
      type: Object,
      default: () => ({}),
    },
  },

  data: () => ({
    segundos: 0,
    milisegundos: 0,
    interval: null,
    intervalMs: null,
  }),

  watch: {
    'item._segundosInicialesParo': 'resetTimer',
    'item.fechaInicio': 'resetTimer',
    'item.status': 'resetTimer',
  },

  computed: {
    ...mapState({ nombreEmpresa: 'nombreEmpresa' }),

    isCazel() {
      const n = this.nombreEmpresa || '';
      return n === 'Cazel' || n === 'CAZEL-TLT' || n === 'LOGISTORAGE';
    },

    inactiveCode() {
      return this.isCazel ? 2 : 0;
    },

    isInactive() {
      return this.isCazel && this.item?.status === this.inactiveCode;
    },

    totalPiezas() {
      return (Number(this.item?.pizasOk) || 0) + (Number(this.item?.piezasScrap) || 0);
    },

    showOeeSection() {
      return !this.isCazel || this.totalPiezas > 0;
    },

    showTcRow() {
      // Solo en máquinas activas (con o sin ODT).
      return this.item?.status === 1;
    },

    showCavidades() {
      return this.item?.status >= 1
        && Array.isArray(this.item?.cavidadesArticulos)
        && this.item.cavidadesArticulos.length > 0;
    },

    mostrarSegundero() {
      return this.item?.status === 3 || this.item?.status === 4;
    },

    showParoBlock() {
      return this.isCazel ? this.item.status > 2 : this.item.status >= 2;
    },

    timerHMS() {
      const s = this.mostrarSegundero ? this.segundos : (this.item.tiempoParoSegundos || 0);
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const sec = s % 60;
      const pad = n => String(n).padStart(2, '0');
      return h > 0 ? pad(h) + ':' + pad(m) + ':' + pad(sec) : pad(m) + ':' + pad(sec);
    },

    timerMS() {
      return String(this.milisegundos).padStart(2, '0');
    },

    circumference() {
      return CIRCUMFERENCE;
    },

    oeeArc() {
      const val = Math.min(100, Math.max(0, Number(this.item?.oee ?? 0)));
      return ((val / 100) * CIRCUMFERENCE).toFixed(2);
    },

    kpis() {
      const fmt = v => {
        const n = Number(v);
        return Number.isFinite(n) ? n.toFixed(1) + '%' : '—';
      };
      return [
        { name: 'Disponib.',  val: Math.min(100, Number(this.item?.disponibilidad ?? 0)), color: '#2B7EFF', display: fmt(this.item?.disponibilidad) },
        { name: 'Eficiencia', val: Math.min(100, Number(this.item?.rendimiento    ?? 0)), color: '#00C472', display: fmt(this.item?.rendimiento)    },
        { name: 'Calidad',    val: Math.min(100, Number(this.item?.calidad        ?? 0)), color: '#FF9F00', display: fmt(this.item?.calidad)        },
      ];
    },

    cardBorderStyle() {
      const c = this.colorEstatus(this.item?.status);
      return {
        border: `1.5px solid ${c}30`,
        boxShadow: `0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.06), 0 0 0 1px ${c}15`,
      };
    },

    pillStyle() {
      const c = this.colorEstatus(this.item?.status);
      return {
        color: c,
        background: c + '14',
        borderColor: c + '40',
      };
    },
  },

  created() {
    this.resetTimer();
    this.iniciar();
    this.iniciarMs();
  },

  destroyed() {
    this.detener();
    this.detenerMs();
  },

  methods: {
    resetTimer() {
      try {
        if (this.item._segundosInicialesParo > 0) {
          this.segundos = this.item._segundosInicialesParo;
        } else if (this.item.fechaInicio) {
          const diff = moment().valueOf() - moment(this.item.fechaInicio).valueOf();
          this.segundos = Math.max(0, Math.trunc(diff / 1000));
          if (isNaN(this.segundos)) this.segundos = 0;
        } else {
          this.segundos = 0;
        }
      } catch {
        this.segundos = 0;
      }
    },

    colorEstatus(status) {
      return (STATUS_MAP[status] || STATUS_MAP[2]).color;
    },

    labelEstatus(status) {
      return (STATUS_MAP[status] || STATUS_MAP[2]).label;
    },

    limpiarOT(valor) {
      if (!valor) return '';
      return valor.replace(/^OT:\s*/i, '');
    },

    validTcText(value) {
      return value != null && value !== '' && value !== '—' && value !== 'â€”';
    },

    tcDisplay(value) {
      return this.validTcText(value) ? value : '—';
    },

    iniciar() {
      if (this.interval) return;
      this.interval = setInterval(() => {
        this.segundos += 1;
        if (!this.isCazel) return;
        const tcm = Number(this.item?.tiempoCambioMoldeSeg);
        if (!Number.isFinite(tcm) || tcm <= 0) return;
        if (this.item.status === 2 && this.segundos >= tcm) {
          this.$emit('status', 3);
          return;
        }
        if (this.item.status === 3 && this.segundos >= tcm + 15 * 60) {
          this.$emit('status', 4);
        }
      }, 1000);
    },

    detener() {
      if (!this.interval) return;
      clearInterval(this.interval);
      this.interval = null;
    },

    iniciarMs() {
      if (this.intervalMs) return;
      this.intervalMs = setInterval(() => {
        if (!this.mostrarSegundero) return;
        this.milisegundos = (this.milisegundos + 1) % 100;
      }, 10);
    },

    detenerMs() {
      if (!this.intervalMs) return;
      clearInterval(this.intervalMs);
      this.intervalMs = null;
    },
  },
};
</script>

<style scoped>
/* ===================================================
   TOKENS
   =================================================== */
.card-maquina {
  --radius-card: 20px;
  --radius-inner: 12px;
  --radius-sm: 8px;
  --clr-bg: #F4F6F9;
  --clr-surface: #F0F2F6;
  --clr-border: rgba(0,0,0,0.07);
  --clr-text-primary: #0D1117;
  --clr-text-secondary: #5A6476;
  --clr-text-muted: #8A95A3;
  --font-primary: 'Space Grotesk', 'Roboto', sans-serif;
  --font-body: 'DM Sans', 'Roboto', sans-serif;
}

/* ===================================================
   CARD BASE
   =================================================== */
.card-maquina {
  background: #FFFFFF;
  border-radius: var(--radius-card);
  position: relative;
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  font-family: var(--font-body);
  display: flex;
  flex-direction: column;
}

.card-maquina:hover {
  transform: translateY(-3px);
}

.side-status-bar {
  position: absolute;
  top: 0;
  left: 0;
  width: 6px;
  height: 100%;
  opacity: 0.92;
}

/* ===================================================
   HEADER
   =================================================== */
.card-header {
  padding: 16px 20px 14px 26px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--clr-border);
  flex-shrink: 0;
}

.machine-id-block {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.machine-label {
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--clr-text-muted);
  font-family: var(--font-primary);
}

.machine-code {
  font-family: var(--font-primary);
  font-size: 22px;
  font-weight: 700;
  color: var(--clr-text-primary);
  letter-spacing: -0.03em;
  line-height: 1.1;
}

.status-pill {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 30px;
  font-family: var(--font-primary);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.03em;
  border: 1.5px solid;
  transition: background 0.3s, color 0.3s;
}

.status-led {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  flex-shrink: 0;
}

/* ===================================================
   OEE RING + KPIS
   =================================================== */
.oee-section {
  padding: 18px 20px 12px;
  display: flex;
  align-items: center;
  gap: 18px;
}

.ring-wrap {
  position: relative;
  width: 90px;
  height: 90px;
  flex-shrink: 0;
}

.ring-wrap svg circle:last-child {
  transition: stroke-dasharray 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.ring-center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  line-height: 1;
  pointer-events: none;
}

.ring-value {
  font-family: var(--font-primary);
  font-size: 19px;
  font-weight: 700;
  color: var(--clr-text-primary);
  letter-spacing: -0.02em;
}

.ring-pct {
  font-size: 10px;
  font-weight: 500;
  color: var(--clr-text-muted);
}

.ring-sub {
  display: block;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--clr-text-muted);
  margin-top: 2px;
  font-family: var(--font-primary);
}

/* KPI rows */
.kpi-trio {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.kpi-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.kpi-label-block {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 76px;
}

.kpi-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.kpi-name {
  font-size: 11px;
  font-weight: 500;
  color: var(--clr-text-secondary);
}

.kpi-bar-wrap {
  flex: 1;
  height: 5px;
  background: var(--clr-surface);
  border-radius: 10px;
  overflow: hidden;
}

.kpi-bar-fill {
  height: 100%;
  border-radius: 10px;
  opacity: 0.75;
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.kpi-value {
  font-family: var(--font-primary);
  font-size: 11px;
  font-weight: 600;
  color: var(--clr-text-primary);
  min-width: 40px;
  text-align: right;
}

/* ===================================================
   TC CHIPS
   =================================================== */
.tc-row {
  padding: 0 20px 14px;
  display: flex;
  gap: 8px;
}

.tc-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 11px;
  border-radius: 8px;
  background: var(--clr-surface);
  border: 1px solid var(--clr-border);
  flex: 1;
  justify-content: center;
}

.tc-chip-label {
  font-size: 10px;
  font-weight: 500;
  color: var(--clr-text-muted);
  font-family: var(--font-primary);
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.tc-chip-value {
  font-size: 12px;
  font-weight: 700;
  font-family: var(--font-primary);
  color: var(--clr-text-primary);
}

.tc-chip-value.qad {
  color: #2B7EFF;
}

/* ===================================================
   DIVIDER
   =================================================== */
.h-divider {
  height: 1px;
  background: var(--clr-border);
  margin: 0 20px;
  flex-shrink: 0;
}

/* ===================================================
   INFO SECTION
   =================================================== */
.info-section {
  padding: 10px 20px 4px;
}

.info-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  padding: 5px 0;
  border-bottom: 1px solid rgba(0,0,0,0.04);
}

.info-row:last-child { border-bottom: none; }

.info-key {
  font-size: 11px;
  font-weight: 500;
  color: var(--clr-text-muted);
  display: flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}

.info-key svg { opacity: 0.7; }

.info-val {
  font-family: var(--font-primary);
  font-size: 12px;
  font-weight: 600;
  color: var(--clr-text-primary);
  text-align: right;
  max-width: 58%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ===================================================
   CAVIDADES POR ARTÍCULO
   =================================================== */
.cavidades-block {
  margin: 8px 0 4px;
  background: var(--clr-surface);
  border: 1px solid var(--clr-border);
  border-radius: var(--radius-inner);
  padding: 10px 12px;
}

.cavidades-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 0;
}

.cavidades-item + .cavidades-item {
  border-top: 1px dashed rgba(0,0,0,0.07);
}

.cavidades-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #2B7EFF;
  opacity: 0.65;
  flex-shrink: 0;
}

.cavidades-art {
  flex: 1;
  font-family: var(--font-primary);
  font-size: 12px;
  font-weight: 600;
  color: var(--clr-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cavidades-badge {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  padding: 2px 9px;
  border-radius: 20px;
  background: rgba(43, 126, 255, 0.10);
  color: #2B7EFF;
  font-family: var(--font-primary);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

/* ===================================================
   PARO SECTION
   =================================================== */
.paro-section {
  margin: 10px 20px 4px;
  background: var(--clr-surface);
  border-radius: var(--radius-inner);
  padding: 13px 15px;
  border-left: 3px solid #FF9F00;
  flex-shrink: 0;
}

.paro-section.paro-np {
  border-left-color: #E83535;
}

.paro-title {
  font-family: var(--font-primary);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--clr-text-muted);
  margin-bottom: 6px;
}

.paro-timer {
  font-family: var(--font-primary);
  font-size: 26px;
  font-weight: 700;
  color: var(--clr-text-primary);
  letter-spacing: -0.03em;
  line-height: 1;
  margin-bottom: 5px;
}

.paro-meta {
  font-size: 11px;
  color: var(--clr-text-secondary);
  line-height: 1.4;
}

.paro-tag {
  display: inline-block;
  padding: 3px 9px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 600;
  font-family: var(--font-primary);
  margin-top: 8px;
  letter-spacing: 0.03em;
  border: 1px solid;
}

.tag-amber { color: #FF9F00; background: rgba(255,159,0,0.10); border-color: rgba(255,159,0,0.30); }
.tag-red   { color: #E83535; background: rgba(232,53,53,0.10);  border-color: rgba(232,53,53,0.30);  }

/* ===================================================
   PRODUCCIÓN
   =================================================== */
.prod-section {
  padding: 12px 20px 18px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(90px, 1fr));
  gap: 8px;
  flex-shrink: 0;
}

.prod-box {
  background: var(--clr-surface);
  border-radius: var(--radius-sm);
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.prod-box-no-ok {
  background: var(--clr-surface);
  border: none;
}

.prod-box-label {
  font-size: 10px;
  font-weight: 500;
  color: var(--clr-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-family: var(--font-primary);
}

.prod-box-value {
  font-family: var(--font-primary);
  font-size: 20px;
  font-weight: 700;
  color: var(--clr-text-primary);
  letter-spacing: -0.02em;
  line-height: 1.1;
}

.prod-box-value.blue   { color: #2B7EFF; }

/* ===================================================
   INACTIVO
   =================================================== */
.inactive-state {
  padding: 32px 20px;
  text-align: center;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.inactive-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: var(--clr-surface);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 10px;
  color: var(--clr-text-muted);
}

.inactive-text {
  font-family: var(--font-primary);
  font-size: 14px;
  font-weight: 600;
  color: var(--clr-text-secondary);
}

.inactive-sub {
  font-size: 12px;
  color: var(--clr-text-muted);
  margin-top: 3px;
}

/* ===================================================
   ANIMACIÓN DE ENTRADA
   =================================================== */
@keyframes card-in {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}

.card-maquina {
  animation: card-in 0.4s ease both;
}

/* ===================================================
   RESPONSIVE
   =================================================== */
@media (max-width: 600px) {
  .machine-code { font-size: 18px; }
  .ring-value   { font-size: 16px; }
  .paro-timer   { font-size: 22px; }
  .prod-box-value { font-size: 18px; }
  .oee-section  { padding: 14px 16px 10px; }
  .card-header  { padding: 14px 16px 12px; }
  .tc-row       { padding: 0 16px 12px; }
}

@media (min-width: 1265px) {
  .machine-code { font-size: 24px; }
  .ring-value   { font-size: 20px; }
  .kpi-name     { font-size: 12px; }
  .kpi-value    { font-size: 12px; }
  .prod-box-value { font-size: 22px; }
}

/* Pantalla 65" CAZEL */
@media screen and (min-width: 3200px) {
  .machine-code    { font-size: 2.8rem; }
  .ring-wrap       { width: 120px; height: 120px; }
  .ring-wrap svg   { width: 120px; height: 120px; }
  .ring-value      { font-size: 26px; }
  .ring-sub        { font-size: 12px; }
  .paro-timer      { font-size: 2.2rem; }
  .prod-box-value  { font-size: 1.8rem; }
  .kpi-name        { font-size: 14px; }
  .kpi-value       { font-size: 14px; }
  .info-key        { font-size: 14px; }
  .info-val        { font-size: 15px; }
  .status-pill     { font-size: 14px; padding: 8px 16px; }
  .tc-chip-label   { font-size: 13px; }
  .tc-chip-value   { font-size: 16px; }
  .prod-box-label  { font-size: 13px; }
  .cavidades-art   { font-size: 15px; }
  .cavidades-badge { font-size: 13px; padding: 3px 12px; }
}

@media (hover: none) {
  .card-maquina:hover { transform: none; }
}

/* ===================================================
   HEADER RIGHT + machine-label-row
   =================================================== */
.machine-label-row {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-bottom: 2px;
}

.machine-cog-icon {
  animation: machine-cog-spin 2.8s linear infinite;
}

@keyframes machine-cog-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.header-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}

/* ===================================================
   BADGE "ACTIVA SIN ODT"
   =================================================== */
.activa-badge {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: 30px;
  font-family: var(--font-primary);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.03em;
  color: #00C472;
  background: rgba(0, 196, 114, 0.10);
  border: 1.5px solid rgba(0, 196, 114, 0.30);
  white-space: nowrap;
}

.activa-led {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #00C472;
  flex-shrink: 0;
  animation: activa-pulse 1.6s ease-in-out infinite;
}

@keyframes activa-pulse {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.35; }
}

/* ===================================================
   MOLDE ROW
   =================================================== */
.info-row-molde {
  background: rgba(43, 126, 255, 0.04);
  border-radius: 6px;
  padding: 5px 8px !important;
  margin: 2px 0;
}

.molde-val {
  font-family: var(--font-primary);
  font-size: 12px;
  font-weight: 700 !important;
  color: #2B7EFF !important;
  letter-spacing: 0.04em;
}

/* ===================================================
   PARO TIMER CON MILISEGUNDOS
   =================================================== */
.paro-timer {
  display: flex;
  align-items: baseline;
  gap: 1px;
  font-family: var(--font-primary);
  line-height: 1;
  margin-bottom: 5px;
}

.paro-timer-hms {
  font-size: 26px;
  font-weight: 700;
  color: var(--clr-text-primary);
  letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
}

.paro-timer-ms {
  font-size: 16px;
  font-weight: 500;
  color: var(--clr-text-muted);
  letter-spacing: 0;
  font-variant-numeric: tabular-nums;
}

</style>
